import { useWebviewContext } from '../webview'

import { cmsStoryblokSlugToPublicUrl } from './cmsStoryblokSlugToPublicUrl'
import { type StoryblokMultilinkType } from './model/storyblokTypes.generated'

/**
 * Returns the public URL for a given Storyblok link object.
 *
 * If the link type is 'story', it parses the story's slug and generates a public URL,
 * optionally adding the tenant host from the path prefix.
 * For other link types, it returns the raw URL from the link object.
 *
 * @param link - The Storyblok link object to resolve.
 * @returns The resolved public URL as a string.
 */
const getStoryblokLinkUrl = (link: StoryblokMultilinkType): string => {
  if (link.linktype === 'story') {
    const parsedUrl = cmsStoryblokSlugToPublicUrl(link.story?.full_slug ?? '', {
      addTenantHostFromPathPrefix: true,
    })

    return parsedUrl
  }

  return link.url
}

/**
 * Returns the appropriate target and URL for a Storyblok link, depending on the webview context.
 *
 * If the page is running inside an app webview and the `appLink` is provided, it will use the `appLink`.
 * Otherwise, it will use the provided `link`.
 *
 * The function uses `useWebviewContext` to determine if the app is running inside a webview.
 * This is useful for handling navigation differently between web and app environments.
 *
 * @param appLink - Optional Storyblok link to use when in a webview context.
 * @param link - The default Storyblok link to use.
 * @returns An object containing the link target (`'_blank'`, `'_self'`, or `undefined`) and the resolved URL.
 */
export const useStoryblokWebOrAppLink = ({
  appLink,
  link,
}: {
  appLink?: StoryblokMultilinkType
  link: StoryblokMultilinkType
}): { target: '_blank' | '_self' | undefined; url: string } => {
  const isWebview = useWebviewContext()
  const linkToShow = isWebview && appLink ? appLink : link

  const url = getStoryblokLinkUrl(linkToShow)

  return { target: linkToShow.target, url }
}
